import { DateTime } from "luxon";
import type {
  CreateUnitPeakPricingWindowInput,
  ProviderPeakPricingWindow,
  ProviderSession,
  ProviderUnit,
} from "./api";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const AR_DAY_LABELS = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

type PricingWindow = ProviderPeakPricingWindow | CreateUnitPeakPricingWindowInput;
type PricingUnit = Pick<ProviderUnit, "base_hourly_rate" | "peak_pricing_windows">;

function scheduleDayForDateTime(value: DateTime) {
  return value.weekday % 7;
}

function buildLocalBoundary(reference: DateTime, timeValue: string) {
  const [hour, minute] = timeValue.split(":").map(Number);
  return reference.set({
    hour,
    minute,
    second: 0,
    millisecond: 0,
  });
}

function resolveHourlyRate(unit: PricingUnit, localAt: DateTime) {
  const currentDay = scheduleDayForDateTime(localAt);
  const currentTime = localAt.toFormat("HH:mm");
  const peakPricingWindows = unit.peak_pricing_windows ?? [];

  for (const window of peakPricingWindows) {
    if (window.day_of_week !== currentDay) {
      continue;
    }

    if (window.start_time <= currentTime && currentTime < window.end_time) {
      return window.hourly_rate;
    }
  }

  return unit.base_hourly_rate;
}

function getNextRateChange(unit: PricingUnit, currentLocal: DateTime) {
  const nextMidnight = currentLocal.startOf("day").plus({ days: 1 });
  const candidates = [nextMidnight];
  const currentDay = scheduleDayForDateTime(currentLocal);
  const peakPricingWindows = unit.peak_pricing_windows ?? [];

  for (const window of peakPricingWindows) {
    if (window.day_of_week !== currentDay) {
      continue;
    }

    const startAt = buildLocalBoundary(currentLocal, window.start_time);
    const endAt = buildLocalBoundary(currentLocal, window.end_time);
    if (currentLocal < startAt) {
      candidates.push(startAt);
    }
    if (currentLocal < endAt) {
      candidates.push(endAt);
    }
  }

  return (
    candidates
    .filter((candidate) => candidate > currentLocal)
    .sort((left, right) => left.toMillis() - right.toMillis())[0] ?? nextMidnight
  );
}

function roundAmount(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function getProviderPricingLocale() {
  if (typeof document !== "undefined" && document.documentElement.lang === "ar") {
    return "ar-EG";
  }

  return "en-US";
}

export function formatPricingAmount(amount: number, locale = getProviderPricingLocale()) {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatPeakPricingWindow(window: PricingWindow) {
  const locale = getProviderPricingLocale();
  const dayLabel = locale === "ar-EG" ? AR_DAY_LABELS[window.day_of_week] : DAY_LABELS[window.day_of_week];
  const perHourLabel = locale === "ar-EG" ? "ساعة" : "hr";
  return `${dayLabel} ${window.start_time}-${window.end_time} · ${formatPricingAmount(window.hourly_rate, locale)}/${perHourLabel}`;
}

export function calculateIntervalTotalAmount(
  unit: PricingUnit,
  venueTimezone: string,
  startIso: string,
  endIso: string,
) {
  let currentLocal = DateTime.fromISO(startIso, { zone: "utc" }).setZone(venueTimezone);
  const endLocal = DateTime.fromISO(endIso, { zone: "utc" }).setZone(venueTimezone);

  if (!currentLocal.isValid || !endLocal.isValid || endLocal <= currentLocal) {
    return 0;
  }

  let total = 0;
  while (currentLocal < endLocal) {
    const nextChange = getNextRateChange(unit, currentLocal);
    const segmentEnd = nextChange < endLocal ? nextChange : endLocal;
    const seconds = Math.max(0, segmentEnd.diff(currentLocal, "seconds").seconds);
    total += (resolveHourlyRate(unit, currentLocal) * seconds) / 3600;
    currentLocal = segmentEnd;
  }

  return roundAmount(total);
}

export function calculateSessionElapsedTotalAmount(
  session: ProviderSession,
  unit: PricingUnit,
  venueTimezone: string,
  elapsedMinutes: number,
) {
  const startedAt = DateTime.fromISO(session.started_at, { zone: "utc" });
  if (!startedAt.isValid || elapsedMinutes <= 0) {
    return 0;
  }

  return calculateIntervalTotalAmount(
    unit,
    venueTimezone,
    session.started_at,
    startedAt.plus({ minutes: elapsedMinutes }).toUTC().toISO() ?? session.started_at,
  );
}

export function calculateSessionPlannedTotalAmount(
  session: ProviderSession,
  unit: PricingUnit,
  venueTimezone: string,
) {
  if (!session.planned_end_at) {
    return null;
  }

  return calculateIntervalTotalAmount(
    unit,
    venueTimezone,
    session.started_at,
    session.planned_end_at,
  );
}